import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  // CardMedia,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormHelperText,
  Grid,
  Grow,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import { InputLabel } from '@mui/material';
import { FormControl } from '@mui/material';
import { Select } from '@mui/material';
import { OutlinedInput } from '@mui/material';
import { MenuItem } from '@mui/material';
import { Button } from '@mui/material';

// third-party
import * as Yup from 'yup';
import { Formik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

// project-imports
import Dot from 'components/@extended/Dot';
import IconButton from 'components/@extended/IconButton';
import MainCard from 'components/MainCard';
import useConfig from 'hooks/useConfig';
import { dispatch, useSelector } from 'store';
import { activeItem, openDrawer } from 'store/reducers/menu';
import { openSnackbar } from 'store/reducers/snackbar';
import useScriptRef from 'hooks/useScriptRef';
import useAuth from 'hooks/useAuth';
// action-manager
import { HubspotAction } from 'utils/action-manager/hubspot/hubspotManager';
import { APIAction } from 'utils/action-manager/api/apiManager';
import handleAction from 'utils/action-manager/actionManager';

// types
import { MenuOrientation, NavActionType, ThemeMode } from 'types/config';
import { LinkTarget, NavActionProps, NavItemType } from 'types/menu';

// assets
import { Add, DiscountShape, Flag, Global, TimerStart } from 'iconsax-react';
import LoadingButton from 'components/@extended/LoadingButton';
import { TransitionProps } from '@mui/material/transitions';
// import img01 from 'assets/images/collabs/fuster.png';

// ==============================|| SUPPORT - FORM ||============================== //

interface SupportFormProps {
  onSuccess?: () => void;
}

const SupportForm = ({ onSuccess }: SupportFormProps) => {
  const scriptedRef = useScriptRef();
  const { user } = useAuth();

  const intl = useIntl();

  const typeOptions = [
    {
      key: 'Problema de rendimiento',
      value: intl.formatMessage({ id: 'support.type.performance' })
    },
    {
      key: 'Facturación/Pago/Suscripción',
      value: intl.formatMessage({ id: 'support.type.billingPaymentSubscription' })
    },
    {
      key: 'Ayuda con el uso de la plataforma',
      value: intl.formatMessage({ id: 'support.type.helpPlatform' })
    },
    {
      key: 'Caida del servicio',
      value: intl.formatMessage({ id: 'support.type.serviceOff' })
    },
    {
      key: 'Datos erroneos en una promoción',
      value: intl.formatMessage({ id: 'support.type.incorrectData' })
    },
    {
      key: 'Consulta genenal',
      value: intl.formatMessage({ id: 'support.type.general' })
    },
    {
      key: 'Problema con Feed XML',
      value: intl.formatMessage({ id: 'support.type.feed' })
    }
  ];

  return (
    <Formik
      initialValues={{
        type: '',
        content: '',
        submit: null
      }}
      validationSchema={Yup.object().shape({
        type: Yup.string().required(`* ${intl.formatMessage({ id: 'common.others.required' })}`),
        content: Yup.string()
          .max(255)
          .required(`* ${intl.formatMessage({ id: 'common.others.required' })}`)
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          let data: any = {
            type: values.type,
            content: values.content
          };

          if (user) {
            data.firstname = user.firstname;
            data.lastname = user.lastname;
            data.email = user.email;
            data.phone = user.phone;
            data.subject = `${user.name} - ${values.type}`;
          }

          const hubspotAction: HubspotAction = {
            type: 'hubspot',
            method: 'support',
            data: data
          };

          handleAction(hubspotAction)
            .then((res) => console.log(res))
            .catch((err) => console.error(err));

          if (scriptedRef.current) {
            setStatus({ success: true });
            setSubmitting(false);
            dispatch(
              openSnackbar({
                open: true,
                message: 'Ticket enviado correctamente.',
                variant: 'alert',
                alert: {
                  color: 'success'
                },
                close: false
              })
            );
          }

          // onSuccess && onSuccess();
          if (onSuccess) {
            setTimeout(() => onSuccess(), 200);
          }
        } catch (err: any) {
          console.error(err);
          if (scriptedRef.current) {
            setStatus({ success: false });
            setErrors({ submit: err.message });
            setSubmitting(false);
          }
        }
      }}
    >
      {({ touched, errors, handleBlur, handleChange, handleSubmit, isSubmitting, values, getFieldProps, setFieldValue }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack spacing={1.25}>
                <InputLabel>
                  <FormattedMessage id="support.type.title" />
                </InputLabel>
                <FormControl fullWidth>
                  <Select
                    id="type"
                    displayEmpty
                    {...getFieldProps('type')}
                    onChange={(event) => setFieldValue('type', event.target.value as string)}
                    input={<OutlinedInput id="select-type" placeholder="Tipo de incidencia" />}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography variant="subtitle1">
                            <FormattedMessage id="support.type.select" />
                          </Typography>
                        );
                      }
                      const itemSelected: any = typeOptions.find((item) => item.key === selected);
                      return <Typography variant="subtitle2">{itemSelected.value}</Typography>;
                    }}
                  >
                    <MenuItem value="" disabled>
                      <FormattedMessage id="support.type.select" />
                    </MenuItem>
                    {typeOptions.map((item: any) => (
                      <MenuItem key={item.key} value={item.key}>
                        <ListItemText primary={item.value} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {touched.type && errors.type && (
                  <FormHelperText error id="personal-type-helper">
                    {errors.type}
                  </FormHelperText>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <InputLabel>
                  <FormattedMessage id="support.description" />
                </InputLabel>
                <TextField
                  id="content"
                  name="content"
                  value={values.content}
                  multiline
                  rows={5}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder={intl.formatMessage({ id: 'support.ticketDescription' })}
                  fullWidth
                />
                {touched.content && errors.content && (
                  <FormHelperText error id="personal-content-helper">
                    {errors.content}
                  </FormHelperText>
                )}
              </Stack>
            </Grid>

            {errors.submit && (
              <Grid item xs={12}>
                <FormHelperText error>{errors.submit}</FormHelperText>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button disableElevation disabled={isSubmitting} fullWidth size="large" type="submit" variant="contained" color="primary">
                <FormattedMessage id="support.send" />
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};

// ==============================|| SUPPORT - FORM ||============================== //

interface AccompanimentFormProps {
  onSuccess?: () => void;
}

const AccompanimentForm = ({ onSuccess }: AccompanimentFormProps) => {
  // const theme = useTheme();
  // const intl = useIntl();

  const { user } = useAuth();

  const [checked, setChecked] = useState(false);

  const handleCheckboxChange = (event: any) => {
    setChecked(event.target.checked);
  };

  const [loading, setLoading] = useState(false);

  const handleSubmit = () => {
    setLoading(true);

    let data: any = {
      email: user?.email,
      phone: user?.phone
    };

    const hubspotAction: HubspotAction = {
      type: 'hubspot',
      method: 'accompaniment',
      data: data
    };

    handleAction(hubspotAction)
      .then((res) => {
        console.log(res);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });

    dispatch(
      openSnackbar({
        open: true,
        message: 'Solicitud enviada correctamente.',
        variant: 'alert',
        alert: {
          color: 'success'
        },
        close: false
      })
    );

    onSuccess && onSuccess();
  };

  const content: any = {
    title: 'Servicio Integral (20% de la comisión)',
    items: [
      {
        title: 'Acompañamiento al Cliente',
        // description: 'F&A acompaña al cliente en la visita a las propiedades, firma de contratos de servicios y poder.'
        description: 'Acompañamiento del cliente en la visita a las propiedades, firma de contratos de servicios y poder.'
      },
      {
        title: 'Gestión de Contratos',
        // description: 'F&A gestiona el contrato con la promotora.'
        description: 'Gestión del contrato con la promotora.'
      },
      {
        title: 'Gestión de Comisiones',
        description:
          // 'F&A se encarga del cobro de las comisiones para la agencia, presentando las facturas en su momento, o reclamándolas extrajudicialmente si llega el caso.'
          'Gestión del cobro de las comisiones para la agencia, presentando las facturas en su momento, o reclamándolas extrajudicialmente si llega el caso.'
      },
      {
        title: 'Alta en la Comunidad Valenciana',
        // description: 'F&A tramita el alta del cliente en la Comunidad Valenciana si es necesario'
        description: 'Tramitación del alta del cliente en la Comunidad Valenciana si es necesario'
      },
      {
        title: 'Asesoría Legal y Fiscal',
        // description: 'F&A asesora en la estructura legal y fiscal.'
        description: 'Asesoramiento en la estructura legal y fiscal.'
      },
      {
        title: 'Pago de Impuestos',
        // description: 'F&A gestiona el pago de los impuestos correspondientes.'
        description: 'Gestión del pago de los impuestos correspondientes.'
      },
      {
        title: 'Intermediación y Litigios',
        // description: 'F&A ofrece servicios de intermediación y asistencia en posibles litigios.'
        description: 'Servicios de intermediación y asistencia en posibles litigios.'
      },
      {
        title: 'Distribución de la Comisión',
        // description: 'F&A 20% de la comisión, mientras que el agente recibe el 80%.'
        description: '20% de la comisión, mientras que el agente recibe el 80%.'
      }
    ]
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" fontWeight={500}>
          {content.title}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <List>
          {content.items.map((item: any, index: any) => (
            <ListItemText primary={item.title} secondary={item.description} key={index} />
          ))}
        </List>
      </Grid>
      <Grid item xs={12}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {/* <CardMedia
            component="img"
            image={img01}
            sx={{
              height: 100,
              width: 'auto',
              objectFit: 'contain',
              filter: theme.palette.mode === ThemeMode.LIGHT ? 'brightness(0)' : '',
              opacity: 0.8,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1
            }}
          /> */}
          <Stack direction="row" spacing={2} alignItems="center" mt={{ xs: 3, md: 0 }}>
            <Checkbox checked={checked} onChange={handleCheckboxChange} color="primary" />
            <Typography variant="body1">
              He leído todos los puntos del Servicio Integral y tengo un cliente interesado en comprar
            </Typography>
          </Stack>
        </Stack>
      </Grid>
      <Grid item xs={12} mt={2}>
        <LoadingButton variant="contained" onClick={handleSubmit} loading={loading} disabled={!checked} fullWidth>
          Solicitar
        </LoadingButton>
      </Grid>
    </Grid>
  );
};

// ==============================|| FIRST STEPS ||============================== //

interface FirstStepsProps {
  theme?: any;
  onClose?: () => void;
}

const FirstSteps = ({ theme, onClose }: FirstStepsProps) => {
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const { user } = useAuth();
  const navigate = useNavigate();

  const steps = [
    {
      id: 'intro',
      label: <FormattedMessage id="firstSteps.slide01.title" />,
      content: (
        <Stack spacing={3}>
          <Typography variant="h5">
            <FormattedMessage id="firstSteps.slide01.text01" />
          </Typography>
          <Typography>
            <FormattedMessage id="firstSteps.slide01.text02" />:
          </Typography>
          <List
            sx={{
              listStyleType: 'disc',
              py: 0,
              pl: matchDownMd ? 2 : 5,
              '& .MuiListItem-root': {
                display: 'list-item',
                p: 0,
                pb: 2,
                '&:last-of-type': {
                  pb: 0
                }
              }
            }}
          >
            <ListItem>
              <FormattedMessage id="firstSteps.slide01.text03" />
            </ListItem>
            <ListItem>
              <FormattedMessage id="firstSteps.slide01.text04" />
            </ListItem>
            <ListItem>
              <FormattedMessage id="firstSteps.slide01.text05" />
            </ListItem>
          </List>
        </Stack>
      )
    },
    {
      id: 'website',
      label: <FormattedMessage id="firstSteps.slide02.title" />,
      content: (
        <Stack spacing={3}>
          <Typography variant="h5">
            <FormattedMessage id="firstSteps.slide02.text01" />
          </Typography>
          <Typography>
            <FormattedMessage id="firstSteps.slide02.text02" />
          </Typography>
          <Global color={theme.palette.primary.main} size={40} style={{ marginLeft: 'auto', marginRight: 'auto' }} />
        </Stack>
      )
    },
    {
      id: 'offers',
      label: <FormattedMessage id="firstSteps.slide03.title" />,
      content: (
        <Stack spacing={3}>
          <Typography variant="h5">
            <FormattedMessage id="firstSteps.slide03.text01" />
          </Typography>
          <Typography>
            <FormattedMessage id="firstSteps.slide03.text02" />
          </Typography>
          <Typography>
            <FormattedMessage id="firstSteps.slide03.text03" />
          </Typography>
          <Typography>
            <FormattedMessage id="firstSteps.slide03.text04" />
          </Typography>
          <DiscountShape color={theme.palette.warning.main} size={40} style={{ marginLeft: 'auto', marginRight: 'auto' }} />
        </Stack>
      )
    },
    {
      id: 'feed',
      label: <FormattedMessage id="firstSteps.slide04.title" />,
      content: (
        <Stack spacing={3}>
          <Typography variant="h5">
            <FormattedMessage id="firstSteps.slide04.text01" />
          </Typography>
          <Typography>
            <FormattedMessage id="firstSteps.slide04.text02" />
          </Typography>
        </Stack>
      )
    },
    {
      id: 'subscription',
      label: <FormattedMessage id="firstSteps.slide05.title" />,
      content: (
        <Stack spacing={3}>
          <Typography variant="h5">
            <FormattedMessage id="firstSteps.slide05.text01" />
          </Typography>
          {user?.currentPlan?.includes('trial') ? (
            <Stack spacing={2}>
              <Card
                sx={{
                  bgcolor: theme.palette.secondary.lighter,
                  color: theme.palette.warning.main,
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: `${theme.palette.warning.main}99 0 0 6px 6px`
                  }
                }}
                onClick={() => {
                  onClose && onClose();
                  navigate('/account/subscription');
                }}
              >
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="h5">
                      <FormattedMessage id="firstSteps.slide05.text03" />
                    </Typography>
                    <List
                      sx={{
                        listStyleType: 'disc',
                        py: 0,
                        pl: matchDownMd ? 2 : 5,
                        '& .MuiListItem-root': {
                          display: 'list-item',
                          p: 0,
                          pb: 2,
                          '&:last-of-type': {
                            pb: 0
                          }
                        }
                      }}
                    >
                      <ListItem>
                        <FormattedMessage id="firstSteps.slide05.text04" />
                      </ListItem>
                      <ListItem>
                        <FormattedMessage id="firstSteps.slide05.text05" />
                      </ListItem>
                      <ListItem>
                        <FormattedMessage id="firstSteps.slide05.text06" />
                      </ListItem>
                    </List>
                    <Typography>
                      <FormattedMessage id="firstSteps.slide05.text07" />
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
              <Card
                sx={{
                  bgcolor: theme.palette.secondary.lighter,
                  color: theme.palette.primary.main,
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: `${theme.palette.primary.main}99 0 0 6px 6px`
                  }
                }}
                onClick={onClose}
              >
                <CardContent>
                  <Typography variant="h5">
                    <FormattedMessage id="firstSteps.slide05.text02" />
                  </Typography>
                </CardContent>
              </Card>
            </Stack>
          ) : (
            <Card
              sx={{
                bgcolor: theme.palette.secondary.lighter,
                color: theme.palette.primary.main,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: `${theme.palette.primary.main}99 0 0 6px 6px`
                }
              }}
              onClick={onClose}
            >
              <CardContent>
                <Typography variant="h5">
                  <FormattedMessage id="common.others.close" />
                </Typography>
              </CardContent>
            </Card>
          )}
        </Stack>
      )
    }
  ];

  const [activeStep, setActiveStep] = useState(0);
  const lastStepIndex = steps.length - 1;
  const safeActiveStep = Math.min(Math.max(activeStep, 0), lastStepIndex);
  const currentStep = steps[safeActiveStep];

  const handleNext = () => {
    setActiveStep((prevStep) => Math.min(prevStep + 1, lastStepIndex));
  };

  const handleBack = () => {
    setActiveStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  // const handleReset = () => {
  //   setActiveStep(0);
  // };

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      {matchDownMd ? (
        <>
          {/* Stepper Component */}
          <Stepper activeStep={safeActiveStep} orientation="vertical">
            {steps.map((step) => (
              <Step key={step.id}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  {/* Slide Content */}
                  <Box sx={{ my: 2 }}>
                    <Card>
                      <CardContent>{currentStep.content}</CardContent>
                    </Card>
                  </Box>
                  {/* Navigation Buttons */}
                  <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    <Button color="inherit" disabled={safeActiveStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
                      <FormattedMessage id="common.others.previous" />
                    </Button>
                    <Box sx={{ flex: '1 1 auto' }} />
                    {safeActiveStep === lastStepIndex ? (
                      // <Button variant="contained" color="success" onClick={onClose}>
                      //   Finalizar
                      // </Button>
                      <></>
                    ) : (
                      <Button variant="contained" onClick={handleNext}>
                        <FormattedMessage id="common.others.next" />
                      </Button>
                    )}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </>
      ) : (
        <>
          {/* Stepper Component */}
          <Stepper activeStep={safeActiveStep} alternativeLabel>
            {steps.map((step) => (
              <Step key={step.id}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Slide Content */}
          <Box sx={{ my: 6 }}>
            <Card>
              <CardContent>{currentStep.content}</CardContent>
            </Card>
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Button color="inherit" disabled={safeActiveStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
              <FormattedMessage id="common.others.previous" />
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            {safeActiveStep === lastStepIndex ? (
              // <Button variant="contained" color="success" onClick={onClose}>
              //   Finalizar
              // </Button>
              <></>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                <FormattedMessage id="common.others.next" />
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

// ==============================|| DIALOGS - VIDEOS ||============================== //

type ActionDialogProps = { open: boolean; onClose: () => void; payload?: any };

function FeedXmlDialog({ open, onClose, payload }: ActionDialogProps) {
  const theme = useTheme();
  const intl = useIntl();

  return (
    <Dialog open={open} onClose={onClose} TransitionComponent={Transition} maxWidth="lg" fullWidth>
      <DialogTitle>
        <FormattedMessage id={payload.title} />
      </DialogTitle>
      <IconButton
        color="error"
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 12,
          top: 12,
          color: theme.palette.grey[500]
        })}
      >
        <Add style={{ transform: 'rotate(45deg)' }} color={theme.palette.error.main} />
      </IconButton>
      <Divider />
      <DialogContent>
        {payload?.videoUrl ? (
          <Box sx={{ width: '100%', height: 0, paddingBottom: '56.25%', position: 'relative' }}>
            <iframe
              src={payload.videoUrl}
              frameBorder="0"
              allow="autoplay; encrypted-media; fullscreen"
              title={intl.formatMessage({ id: payload.title })}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                borderRadius: 10
              }}
              allowFullScreen
            />
          </Box>
        ) : (
          <p>La URL del video no es válida. Por favor, verifica el enlace.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OffersDialog({ open, onClose, payload }: ActionDialogProps) {
  const theme = useTheme();
  const intl = useIntl();

  return (
    <Dialog open={open} onClose={onClose} TransitionComponent={Transition} maxWidth="lg" fullWidth>
      <DialogTitle>
        <FormattedMessage id={payload.title} />
      </DialogTitle>
      <IconButton
        color="error"
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 12,
          top: 12,
          color: theme.palette.grey[500]
        })}
      >
        <Add style={{ transform: 'rotate(45deg)' }} color={theme.palette.error.main} />
      </IconButton>
      <Divider />
      <DialogContent>
        {payload?.videoUrl ? (
          <Box sx={{ width: '100%', height: 0, paddingBottom: '56.25%', position: 'relative' }}>
            <iframe
              src={payload.videoUrl}
              frameBorder="0"
              allow="autoplay; encrypted-media; fullscreen"
              title={intl.formatMessage({ id: payload.title })}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                borderRadius: 10
              }}
              allowFullScreen
            />
          </Box>
        ) : (
          <p>La URL del video no es válida. Por favor, verifica el enlace.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AIDescriptionToolDialog({ open, onClose, payload }: ActionDialogProps) {
  const theme = useTheme();
  const intl = useIntl();

  return (
    <Dialog open={open} onClose={onClose} TransitionComponent={Transition} maxWidth="lg" fullWidth>
      <DialogTitle>
        <FormattedMessage id={payload.title} />
      </DialogTitle>
      <IconButton
        color="error"
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 12,
          top: 12,
          color: theme.palette.grey[500]
        })}
      >
        <Add style={{ transform: 'rotate(45deg)' }} color={theme.palette.error.main} />
      </IconButton>
      <Divider />
      <DialogContent>
        {payload?.videoUrl ? (
          <Box sx={{ width: '100%', height: 0, paddingBottom: '56.25%', position: 'relative' }}>
            <iframe
              src={payload.videoUrl}
              frameBorder="0"
              allow="autoplay; encrypted-media; fullscreen"
              title={intl.formatMessage({ id: payload.title })}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                borderRadius: 10
              }}
              allowFullScreen
            />
          </Box>
        ) : (
          <p>La URL del video no es válida. Por favor, verifica el enlace.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  //   return <Slide direction="up" ref={ref} {...props} />;
  return <Grow ref={ref} {...props} />;
});

const ACTION_DIALOGS: Record<string, React.FC<ActionDialogProps>> = {
  'feed-xml': FeedXmlDialog,
  offers: OffersDialog,
  'ai-description-tool': AIDescriptionToolDialog
};

const SALES_SERVICE_APP = 'sales_service';
const SALES_SERVICE_URL = 'http://sales-service.habihub.com';

const parsePartnerApps = (value?: string): string[] => {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
  } catch {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
};

// ==============================|| NAVIGATION - ITEM ||============================== //

interface Props {
  item: NavItemType;
  level: number;
}

const NavItem = ({ item, level }: Props) => {
  const theme = useTheme();
  const intl = useIntl();
  // const mode = theme.palette.mode;
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const { drawerOpen, openItem } = useSelector((state) => state.menu);
  const { menuOrientation } = useConfig();

  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const isSelected = openItem.findIndex((id: string) => id === item.id) > -1;

  const Icon = item.icon!;
  const itemIcon = item.icon ? <Icon variant="Bulk" size={drawerOpen ? 20 : 22} /> : false;

  const { pathname } = useLocation();

  // active menu item on page load
  useEffect(() => {
    if (pathname && pathname.includes('product-details')) {
      if (item.url && item.url.includes('product-details')) {
        dispatch(activeItem({ openItem: [item.id] }));
      }
    }

    if (pathname && pathname.includes('kanban')) {
      if (item.url && item.url.includes('kanban')) {
        dispatch(activeItem({ openItem: [item.id] }));
      }
    }

    if (pathname === item.url) {
      dispatch(activeItem({ openItem: [item.id] }));
    }
    // eslint-disable-next-line
  }, [pathname]);

  const textColor = theme.palette.mode === ThemeMode.DARK ? 'secondary.400' : 'secondary.main';
  const iconSelectedColor = 'primary.main';

  // [ FIRST STEPS ]
  const [isFirstSteps, setIsFirstSteps] = useState(false);
  const { user } = useAuth();
  const isSalesServiceItem = item.id === 'sales-service';

  const closeFirstSteps = () => setIsFirstSteps(false);

  useEffect(() => {
    const drawerOpened = sessionStorage.getItem('drawerOpened');

    if (!drawerOpened && (!user?.firstLogin || user.firstLogin !== 'true')) {
      setIsFirstSteps(true);
      sessionStorage.setItem('drawerOpened', 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.firstLogin]);

  // [ SUPPORT ]
  const [isSupport, setIsSupport] = useState(false);

  // [ ACCOMPANIMENT ]
  const [isAccompaniment, setIsAccompaniment] = useState(false);

  // [ SALES SERVICE ]
  const [isSalesService, setIsSalesService] = useState(false);
  const [isSalesServiceLoading, setIsSalesServiceLoading] = useState(false);
  const [isSalesServiceActivated, setIsSalesServiceActivated] = useState(false);

  const closeSalesService = () => setIsSalesService(false);

  const redirectToSalesService = () => {
    window.open(SALES_SERVICE_URL, '_blank', 'noopener,noreferrer');
  };

  const handleSalesServiceAccess = async () => {
    const partnerApps = parsePartnerApps(user?.partnerApps);

    if (isSalesServiceActivated || partnerApps.includes(SALES_SERVICE_APP)) {
      redirectToSalesService();
      return;
    }

    if (!user?.id) {
      dispatch(
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'salesService.error' }),
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
      return;
    }

    setIsSalesServiceLoading(true);
    const salesServiceWindow = window.open('', '_blank');
    if (salesServiceWindow) {
      salesServiceWindow.opener = null;
    }

    try {
      const apiAction: APIAction = {
        type: 'api',
        entity: 'cognito',
        method: 'partner-apps',
        data: {
          sub: user.id,
          action: 'add',
          app: SALES_SERVICE_APP
        }
      };

      await handleAction(apiAction);
      setIsSalesServiceActivated(true);
      dispatch(
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'salesService.enabled' }),
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: false
        })
      );
      closeSalesService();
      if (salesServiceWindow) {
        salesServiceWindow.location.href = SALES_SERVICE_URL;
      } else {
        redirectToSalesService();
      }
    } catch (err) {
      salesServiceWindow?.close();
      console.error('Sales Service activation failed:', err);
      dispatch(
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'salesService.error' }),
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
    } finally {
      setIsSalesServiceLoading(false);
    }
  };

  const handleClick = (event?: React.MouseEvent) => {
    if (isSalesServiceItem) {
      event?.preventDefault();

      if (isSalesServiceActivated || parsePartnerApps(user?.partnerApps).includes(SALES_SERVICE_APP)) {
        redirectToSalesService();
      } else {
        setIsSalesService(true);
      }

      return;
    }

    switch (item.id) {
      case 'first-steps':
        setIsFirstSteps(true);
        break;
      case 'support':
        setIsSupport(true);
        break;
      case 'accompaniment':
        setIsAccompaniment(true);
        break;
      default:
        break;
    }
  };

  // [ DIALOG ] - VIDEOS
  const [openActionDialog, setOpenActionDialog] = useState<{ key: string; payload?: any } | null>(null);

  const handleActionClick = (action: NavActionProps, e: React.MouseEvent) => {
    e.stopPropagation();
    switch (action.type) {
      case NavActionType.FUNCTION:
        action.function?.();
        break;
      case NavActionType.EVENT:
        setOpenActionDialog({ key: action.event || '', payload: action.payload });
        break;
      case NavActionType.LINK:
        // si quieres abrir con window.open:
        if (action.url) {
          action.target ? window.open(action.url, '_blank', 'noopener,noreferrer') : (window.location.href = action.url);
        }
        break;
    }
  };

  const closeActionDialog = () => setOpenActionDialog(null);

  const ActiveDialog = openActionDialog ? ACTION_DIALOGS[openActionDialog.key] : null;

  return (
    <>
      {menuOrientation === MenuOrientation.VERTICAL || downLG ? (
        <Box sx={{ position: 'relative' }}>
          <ListItemButton
            component={Link}
            to={item.url!}
            target={itemTarget}
            disabled={item.disabled}
            selected={isSelected}
            onClick={handleClick}
            sx={{
              zIndex: 1201,
              pl: drawerOpen ? `${level * 20}px` : 1.5,
              py: !drawerOpen && level === 1 ? 1.25 : 1,
              ...(drawerOpen && {
                '&:hover': {
                  bgcolor: 'transparent'
                },
                '&.Mui-selected': {
                  '&:hover': {
                    bgcolor: 'transparent'
                  },
                  bgcolor: 'transparent'
                }
              }),
              ...(drawerOpen &&
                level === 1 && {
                  mx: 1.25,
                  my: 0.5,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: theme.palette.mode === ThemeMode.DARK ? 'divider' : 'secondary.200'
                  },
                  '&.Mui-selected': {
                    color: iconSelectedColor,
                    '&:hover': {
                      color: iconSelectedColor
                    }
                  }
                }),
              ...(!drawerOpen && {
                px: 2.75,
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: 'transparent'
                },
                '&.Mui-selected': {
                  '&:hover': {
                    bgcolor: 'transparent'
                  },
                  bgcolor: 'transparent'
                }
              })
            }}
            {...(downLG && {
              onClick: (event: React.MouseEvent) => {
                handleClick(event);
                dispatch(openDrawer(false));
              }
            })}
          >
            {itemIcon && (
              <ListItemIcon
                sx={{
                  minWidth: 38,
                  color: isSelected ? iconSelectedColor : textColor,
                  ...(!drawerOpen &&
                    level === 1 && {
                      borderRadius: 1,
                      width: 46,
                      height: 46,
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.light' : 'secondary.200'
                      }
                    }),
                  ...(!drawerOpen &&
                    isSelected && {
                      bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.100' : 'primary.lighter',
                      '&:hover': {
                        bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.200' : 'primary.lighter'
                      }
                    })
                }}
              >
                {itemIcon}
              </ListItemIcon>
            )}

            {!itemIcon && drawerOpen && (
              <ListItemIcon
                sx={{
                  minWidth: 30
                }}
              >
                <Dot size={isSelected ? 6 : 5} color={isSelected ? 'primary' : 'secondary'} />
              </ListItemIcon>
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && (
              <ListItemText
                primary={
                  <Typography variant="h6" sx={{ color: isSelected ? iconSelectedColor : textColor, fontWeight: isSelected ? 500 : 400 }}>
                    {item.title}
                  </Typography>
                }
              />
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
              <Chip
                color={item.chip.color}
                variant={item.chip.variant}
                size={item.chip.size}
                label={item.chip.label}
                avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
              />
            )}
          </ListItemButton>
          {/* {(drawerOpen || (!drawerOpen && level !== 1)) &&
            item?.actions &&
            item?.actions.map((action, index) => {
              const ActionIcon = action?.icon!;
              const callAction = action?.function;
              return (
                <IconButton
                  key={index}
                  {...(action.type === NavActionType.FUNCTION && {
                    onClick: (event) => {
                      event.stopPropagation();
                      callAction();
                    }
                  })}
                  {...(action.type === NavActionType.LINK && {
                    component: Link,
                    to: action.url,
                    target: action.target ? '_blank' : '_self'
                  })}
                  color="secondary"
                  variant="outlined"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 20,
                    zIndex: 1202,
                    width: 20,
                    height: 20,
                    p: 0.25,
                    color: 'secondary.dark',
                    borderColor: isSelected ? 'primary.light' : 'secondary.light',
                    svg: {
                      color: isSelected ? 'primary.light' : 'secondary.light'
                    },
                    '&:hover': {
                      borderColor: isSelected ? 'primary.main' : 'secondary.main',
                      svg: {
                        color: isSelected ? 'primary.main' : 'secondary.main'
                      }
                    }
                  }}
                >
                  <ActionIcon
                    size={12}
                    // color={mode === ThemeMode.DARK ? theme.palette.secondary[400] : theme.palette.secondary.main}
                    style={{
                      marginLeft: 1
                    }}
                  />
                </IconButton>
              );
            })} */}
          {(drawerOpen || (!drawerOpen && level !== 1)) &&
            item?.actions?.map((action, index) => {
              const ActionIcon = action.icon as any;
              return (
                <IconButton
                  key={index}
                  onClick={(e) => handleActionClick(action, e)}
                  color="secondary"
                  variant="outlined"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 20 + index * 24, // <- separa iconos
                    zIndex: 1202,
                    width: 20,
                    height: 20,
                    p: 0.25,
                    color: 'secondary.dark',
                    borderColor: isSelected ? 'primary.light' : 'secondary.light',
                    svg: { color: isSelected ? 'primary.light' : 'secondary.light' },
                    '&:hover': {
                      borderColor: isSelected ? 'primary.main' : 'secondary.main',
                      svg: { color: isSelected ? 'primary.main' : 'secondary.main' }
                    }
                  }}
                >
                  <ActionIcon variant="Bulk" size={12} style={{ marginLeft: 1 }} color={theme.palette.primary.main} />
                </IconButton>
              );
            })}
        </Box>
      ) : (
        <ListItemButton
          component={Link}
          to={item.url!}
          target={itemTarget}
          disabled={item.disabled}
          selected={isSelected}
          onClick={handleClick}
          sx={{
            zIndex: 1201,
            ...(drawerOpen && {
              '&:hover': {
                bgcolor: 'transparent'
              },
              '&.Mui-selected': {
                bgcolor: 'transparent',
                color: iconSelectedColor,
                '&:hover': {
                  color: iconSelectedColor,
                  bgcolor: 'transparent'
                }
              }
            }),
            ...(!drawerOpen && {
              '&:hover': {
                bgcolor: 'transparent'
              },
              '&.Mui-selected': {
                '&:hover': {
                  bgcolor: 'transparent'
                },
                bgcolor: 'transparent'
              }
            })
          }}
        >
          {itemIcon && (
            <ListItemIcon
              sx={{
                minWidth: 36,
                ...(!drawerOpen && {
                  borderRadius: 1,
                  width: 36,
                  height: 36,
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  '&:hover': {
                    bgcolor: 'transparent'
                  }
                }),
                ...(!drawerOpen &&
                  isSelected && {
                    bgcolor: 'transparent',
                    '&:hover': {
                      bgcolor: 'transparent'
                    }
                  })
              }}
            >
              {itemIcon}
            </ListItemIcon>
          )}

          <ListItemText
            primary={
              <Typography variant="h6" sx={{ color: isSelected ? iconSelectedColor : textColor, fontWeight: isSelected ? 500 : 400 }}>
                {item.title}
              </Typography>
            }
          />
          {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
              sx={{ ml: 1 }}
            />
          )}
        </ListItemButton>
      )}
      <Drawer
        anchor="right"
        onClose={closeFirstSteps}
        open={isFirstSteps}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: matchDownMd ? '100%' : '50%'
          }
        }}
      >
        <MainCard content={false} border={false}>
          <Stack direction="row" justifyContent="space-between" sx={{ px: 3, py: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TimerStart variant="Bulk" />
              <Typography variant="h5">
                <FormattedMessage id="firstSteps.title" />
              </Typography>
            </Stack>
            <IconButton size="small" aria-label="close" color="error" onClick={closeFirstSteps}>
              <Add style={{ transform: 'rotate(45deg)' }} />
            </IconButton>
          </Stack>
          <Divider />
          <Box sx={{ height: 'calc(100vh - 57px)', p: 3, overflowY: 'auto' }}>
            <FirstSteps theme={theme} onClose={closeFirstSteps} />
          </Box>
        </MainCard>
      </Drawer>
      <Drawer
        anchor="right"
        onClose={() => setIsSupport(!isSupport)}
        open={isSupport}
        keepMounted
        ModalProps={{
          disableEnforceFocus: true
        }}
        PaperProps={{
          sx: {
            width: matchDownMd ? '100%' : '40%',
            '&>*': {
              bgcolor: `${theme.palette.secondary.lighter} !important`
            }
          }
        }}
      >
        <MainCard content={false} border={false}>
          <Stack direction="row" justifyContent="space-between" sx={{ px: 3, py: 2 }}>
            <Typography variant="h5">
              <FormattedMessage id="support.title" />
            </Typography>
            <IconButton size="small" aria-label="close" color="error" onClick={() => setIsSupport(!isSupport)}>
              <Add style={{ transform: 'rotate(45deg)' }} />
            </IconButton>
          </Stack>
          <Box sx={{ height: 'calc(100vh - 57px)', px: 3, py: 2, overflowY: 'auto' }}>
            <SupportForm onSuccess={() => setIsSupport(!isSupport)} />
          </Box>
        </MainCard>
      </Drawer>
      <Drawer
        anchor="right"
        onClose={() => setIsAccompaniment(!isAccompaniment)}
        open={isAccompaniment}
        PaperProps={{
          sx: {
            width: matchDownMd ? '100%' : '40%',
            '&>*': {
              bgcolor: `${theme.palette.secondary.lighter} !important`
            }
          }
        }}
      >
        <MainCard content={false} border={false}>
          <Stack direction="row" justifyContent="space-between" sx={{ px: 3, py: 2 }}>
            <Typography variant="h5">
              <FormattedMessage id="accompaniment.title" />
            </Typography>
            <IconButton size="small" aria-label="close" color="error" onClick={() => setIsAccompaniment(!isAccompaniment)}>
              <Add style={{ transform: 'rotate(45deg)' }} />
            </IconButton>
          </Stack>
          <Box sx={{ height: 'calc(100vh - 57px)', px: 3, py: 2, overflowY: 'auto' }}>
            <AccompanimentForm onSuccess={() => setIsAccompaniment(!isAccompaniment)} />
          </Box>
        </MainCard>
      </Drawer>
      <Drawer
        anchor="right"
        onClose={closeSalesService}
        open={isSalesService}
        PaperProps={{
          sx: {
            width: matchDownMd ? '100%' : '40%',
            '&>*': {
              bgcolor: `${theme.palette.secondary.lighter} !important`
            }
          }
        }}
      >
        <MainCard content={false} border={false}>
          <Stack direction="row" justifyContent="space-between" sx={{ px: 3, py: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Flag variant="Bulk" size={22} color={theme.palette.primary.main} />
              <Typography variant="h5">
                <FormattedMessage id="common.menu.tools.salesService" />
              </Typography>
            </Stack>
            <IconButton size="small" aria-label="close" color="error" onClick={closeSalesService}>
              <Add style={{ transform: 'rotate(45deg)' }} />
            </IconButton>
          </Stack>
          <Divider />
          <Box sx={{ height: 'calc(100vh - 57px)', px: 3, py: 2, overflowY: 'auto' }}>
            <Stack spacing={3}>
              <Stack spacing={2}>
                <Typography variant="h5" fontWeight={500}>
                  <FormattedMessage id="salesService.question" />
                </Typography>
                <Typography>
                  <FormattedMessage id="salesService.description" />
                </Typography>
              </Stack>
              <List
                sx={{
                  listStyleType: 'disc',
                  py: 0,
                  pl: matchDownMd ? 2 : 3,
                  '& .MuiListItem-root': {
                    display: 'list-item',
                    p: 0,
                    pb: 1.5,
                    '&:last-of-type': {
                      pb: 0
                    }
                  }
                }}
              >
                <ListItem>
                  <FormattedMessage id="salesService.points.clientRemainsYours" />
                </ListItem>
                <ListItem>
                  <FormattedMessage id="salesService.points.detailsPrivate" />
                </ListItem>
                <ListItem>
                  <FormattedMessage id="salesService.points.noFixedCosts" />
                </ListItem>
              </List>
              <Typography variant="h5">
                <FormattedMessage id="salesService.welcome" />
              </Typography>
              <LoadingButton variant="contained" onClick={handleSalesServiceAccess} loading={isSalesServiceLoading} fullWidth>
                <FormattedMessage id="salesService.access" />
              </LoadingButton>
            </Stack>
          </Box>
        </MainCard>
      </Drawer>
      {/* Dialog genérico para acciones */}
      {ActiveDialog && <ActiveDialog open onClose={closeActionDialog} payload={openActionDialog?.payload} />}
    </>
  );
};

export default NavItem;
